import type { LocalizedString } from '../utils/localization';

export const CASE_OUT_OF_SUPPORT_DECLINED = 'Out of support - Declined';
export const REOPEN_CASE_WINDOW_DAYS = 15;
export const FOLLOWUP_CASE_WINDOW_DAYS = 180;
export const ELEVATE_ESCALATION_WINDOW_HOURS = 12;

export const LICENSE_EXPIRED_WINDOW_DAYS = 14;

export const MAX_EMAIL_BODY_LENGTH = 100000;

export enum SupportCaseType {
  TECHNICAL_SUPPORT = 'TECHNICAL_SUPPORT',
  LICENSE_QUERIES_AND_ACTIVATION = 'LICENSE_QUERIES_AND_ACTIVATION',
}

export const LICENSE_VALIDATION_API_ERROR: LocalizedString = {
  textKey: 'license_validation_api_error',
  fallbackText: 'There was an error while validating the license code!',
};

export const INVALID_ENTITLEMENT_CASE_CREATION_ERROR: LocalizedString = {
  textKey: 'invalid_entitlement_case_creation_error',
  fallbackText: 'For support on this product, visit <a href=\'{{sapSupportUrl}}\' target="_blank">SAP Support</a>.',
};

export const INVALID_LICENSE: LocalizedString = {
  textKey: 'invalid_license_error',
  fallbackText: 'This license code cannot be used for Customer Portal access.',
};

export const VALID_LICENSE: LocalizedString = {
  textKey: 'valid_license_message',
  fallbackText: 'The license code was successfully validated!',
};

export const TRIAL_LICENSE_ERROR: LocalizedString = {
  textKey: 'trial_license_error',
  fallbackText: 'Trial Licenses cannot be used. Please use a full license.',
};

export const INVALID_SITE_URL: LocalizedString = {
  textKey: 'invalid_site_url_error',
  fallbackText: 'The URL is not a valid URL. Please make sure to follow the format mentioned above.',
};

export const LICENSE_VALIDATION_WARNING: LocalizedString = {
  textKey: 'license_id_validation_warning',
  fallbackText: 'Your license code and deployment type don\'t match, but this won\'t stop your support request. Double-check the code for faster resolution, or proceed if you\'re sure it\'s correct.',
};

export const SELF_REGISTRATION_DISABLED_ERROR: LocalizedString = {
  textKey: 'self_registration_disabled_error',
  fallbackText: 'Your administrator does not allow users to register for this account. Please reach out to your admin. If you believe this may be an error, please contact your UiPath Account team for assistance.<br/><br/>To contact support, please visit our <a href=\'{{supportFormPath}}\'>Support Request</a> form. Note that if your company manages your implementation, you should contact your internal IT group for assistance.',
};

export const INVALID_ENTITLEMENT_ERROR: LocalizedString = {
  textKey: 'invalid_entitlement_error',
  fallbackText: 'Unable to register for Customer Portal with the provided licenses. <br/><br/>For support on this product, visit <a href=\'{{sapSupportUrl}}\' target="_blank">SAP Support</a>.',
};

export const MAX_DESCRIPTION_LENGTH: number = 32000;

export const supportCaseTypes = [
  {
    type: SupportCaseType.TECHNICAL_SUPPORT,
    title: {
      textKey: 'support_form_case_type_technical_support_title',
      fallbackText: 'Technical Support',
    },
    description: {
      textKey: 'support_form_case_type_technical_support_description',
      fallbackText:
        'Please use this form to submit technical support requests related to your licensed UiPath products.',
    },
  },
  {
    type: SupportCaseType.LICENSE_QUERIES_AND_ACTIVATION,
    title: {
      textKey: 'support_form_case_type_license_and_activation_title',
      fallbackText: 'License Queries & Activation',
    },
    description: {
      textKey: 'support_form_case_type_license_and_activation_description',
      fallbackText:
        'Please use the form below to submit Licensing Queries & Activation requests related to UiPath Studio, UiPath Robot or UiPath Orchestrator.',
    },
  },
];

export const EOL_END_OF_MAINSTREAM_SUPPORT_ONE_YEAR: LocalizedString = {
  textKey: 'support_form_eol_end_of_mainstream_support_one_year',
  fallbackText: 'The selected version has reached End of Mainstream Support. Once Mainstream Support ends, Extended Support is provided for one additional year on a best-effort basis. Please view our <a href=\'https://docs.uipath.com/overview/other/latest/overview/product-lifecycle\' target=\'_blank\'>Product Lifecycle</a> page for release information. View the <a href=\'https://docs.uipath.com\' target=\'_blank\'>Product Documentation</a> for help with installing and upgrading.',
};

export const EOL_END_OF_MAINSTREAM_SUPPORT_TWO_YEARS: LocalizedString = {
  textKey: 'support_form_eol_end_of_mainstream_support_two_years',
  fallbackText: 'The selected version has reached End of Mainstream Support. Once Mainstream Support ends, Extended Support is provided for two additional years on a best-effort basis. Please view our <a href=\'https://docs.uipath.com/overview/other/latest/overview/product-lifecycle\' target=\'_blank\'>Product Lifecycle</a> page for release information. View the <a href=\'https://docs.uipath.com\' target=\'_blank\'>Product Documentation</a> for help with installing and upgrading.',
};

export const EOL_END_OF_EXTENDED_SUPPORT_BEFORE_THRESHOLD: LocalizedString = {
  textKey: 'support_form_eol_end_of_extended_support_before_threshold',
  fallbackText: 'As per our standard <a href=\'https://docs.uipath.com/overview/other/latest/overview/general-support-terms\' target=\'_blank\'>lifecycle policies</a>, the selected version has reached End of Support and incident support and critical security fixes will be provided as best effort until Oct. 31, 2023. View the <a href=\'https://docs.uipath.com\' target=\'_blank\'>Product Documentation</a> for help with installing and upgrading.',
};

export const EOL_END_OF_EXTENDED_SUPPORT_AFTER_THRESHOLD: LocalizedString = {
  textKey: 'support_form_eol_end_of_extended_support_after_threshold',
  fallbackText: 'As per our standard <a href=\'https://docs.uipath.com/overview/other/latest/overview/general-support-terms\' target=\'_blank\'>lifecycle policies</a>, the selected version has reached End of Support, and is no longer eligible for assisted support. The support request will be reviewed by the support team for a final decision. View <a href=\'https://docs.uipath.com\' target=\'_blank\'>Product Documentation</a> for help with planning, installing and upgrading.',
};

export const LICENSE_PLACEHOLDER: LocalizedString = {
  textKey: 'support_from_license_code_placeholder',
  fallbackText: 'Select a license if listed, or manually type in and hit ↵ Enter',
};

export const LICENSE_HELP_TEXT: LocalizedString = {
  textKey: 'support_form_license_code_help_text',
  fallbackText: 'Please provide your license number to expedite the resolution process. You can select from the dropdown list or enter the license number manually if your product isn\'t listed',
};

export enum DeploymentTypeOption {
  AUTOMATION_CLOUD = 'Automation Cloud',
  AUTOMATION_SUITE = 'Automation Suite',
  STANDALONE_SERVER_PRODUCT = 'Standalone Server Product',
  DESKTOP_VIRTUAL_DESKTOP_CLIENT = 'Desktop/Virtual Desktop Client',
  NOT_APPLICABLE = 'Not Applicable',
}

export enum LicenseDeploymentType {
  SERVICE_FABRIC = 'service-fabric',
  CLOUD = 'cloud',
  STUDIO = 'Studio',
  TCMSI = 'TCMSI',
  PMMSI = 'PMMSI',
  ORCHESTRATORMSI = 'OrchestratorMSI',
  CVMSI = 'CVMSI',
  ROBOT = 'Robot',
  AHSTANDALONE = 'AHStandalone',
  NO_DEPLOYMENT_TYPE = 'NoDeploymentType',
  DEDICATED_CLOUD = 'dedicated',
}

export enum LicenseBundleCode {
  CRPA = 'CRPA',
  PSF = 'PSF',
  UIP = 'UIP',
  UIP_S_L = 'UIP-S-L',
  ATTNU_L = 'ATTNU-L',
  ATTNL = 'ATTNL',
  ATTNU = 'ATTNU',
  UNATT = 'UNATT',
  NONPR = 'NONPR',
  EE = 'EE',
  STD_L = 'STD-L',
  STDNL = 'STDNL',
  STD = 'STD',
  PG_PLATFORM = 'PG-PLATFORM',
  CEH = 'CEH',
  CV_SERVER = 'CV-SERVER',
}

export const LicenseAndDeploymentTypeMatch = {
  [DeploymentTypeOption.AUTOMATION_CLOUD]: [
    LicenseDeploymentType.CLOUD,
    LicenseDeploymentType.NO_DEPLOYMENT_TYPE,
    LicenseDeploymentType.DEDICATED_CLOUD,
  ],
  [DeploymentTypeOption.AUTOMATION_SUITE]: [
    LicenseDeploymentType.SERVICE_FABRIC,
    LicenseDeploymentType.NO_DEPLOYMENT_TYPE,
  ],
  [DeploymentTypeOption.STANDALONE_SERVER_PRODUCT]: [
    LicenseDeploymentType.STUDIO,
    LicenseDeploymentType.TCMSI,
    LicenseDeploymentType.PMMSI,
    LicenseDeploymentType.ORCHESTRATORMSI,
    LicenseDeploymentType.CVMSI,
    LicenseDeploymentType.ROBOT,
    LicenseDeploymentType.AHSTANDALONE,
    LicenseDeploymentType.NO_DEPLOYMENT_TYPE,
  ],
  [DeploymentTypeOption.DESKTOP_VIRTUAL_DESKTOP_CLIENT]: [
    LicenseDeploymentType.STUDIO,
    LicenseDeploymentType.TCMSI,
    LicenseDeploymentType.PMMSI,
    LicenseDeploymentType.ORCHESTRATORMSI,
    LicenseDeploymentType.CVMSI,
    LicenseDeploymentType.ROBOT,
    LicenseDeploymentType.AHSTANDALONE,
    LicenseDeploymentType.NO_DEPLOYMENT_TYPE,
  ],
  [DeploymentTypeOption.NOT_APPLICABLE]: [
    LicenseDeploymentType.NO_DEPLOYMENT_TYPE,
  ],
};

export const LicenseAndBundleCodeMatch = {
  [DeploymentTypeOption.AUTOMATION_CLOUD]: [
    LicenseBundleCode.CRPA,
    LicenseBundleCode.PSF,
    LicenseBundleCode.UIP_S_L,
  ],
  [DeploymentTypeOption.AUTOMATION_SUITE]: [
    LicenseBundleCode.CRPA,
    LicenseBundleCode.PSF,
    LicenseBundleCode.UIP_S_L,
  ],
  [DeploymentTypeOption.STANDALONE_SERVER_PRODUCT]: [
    LicenseBundleCode.UIP,
    LicenseBundleCode.CRPA,
    LicenseBundleCode.ATTNU_L,
    LicenseBundleCode.ATTNL,
    LicenseBundleCode.ATTNU,
    LicenseBundleCode.UNATT,
    LicenseBundleCode.NONPR,
    LicenseBundleCode.EE,
    LicenseBundleCode.STD_L,
    LicenseBundleCode.STDNL,
    LicenseBundleCode.STD,
    LicenseBundleCode.PG_PLATFORM,
    LicenseBundleCode.CEH,
    LicenseBundleCode.CV_SERVER,
    LicenseBundleCode.UIP_S_L,
  ],
  [DeploymentTypeOption.DESKTOP_VIRTUAL_DESKTOP_CLIENT]: [
    LicenseBundleCode.UIP,
    LicenseBundleCode.CRPA,
    LicenseBundleCode.ATTNU_L,
    LicenseBundleCode.ATTNL,
    LicenseBundleCode.ATTNU,
    LicenseBundleCode.UNATT,
    LicenseBundleCode.NONPR,
    LicenseBundleCode.EE,
    LicenseBundleCode.STD_L,
    LicenseBundleCode.STDNL,
    LicenseBundleCode.STD,
    LicenseBundleCode.PG_PLATFORM,
    LicenseBundleCode.CEH,
    LicenseBundleCode.CV_SERVER,
    LicenseBundleCode.UIP_S_L,
  ],
  [DeploymentTypeOption.NOT_APPLICABLE]: [
    LicenseBundleCode.CRPA,
    LicenseBundleCode.UIP_S_L,
  ],
};

export enum SupportFormSection {
  DESCRIBE_ISSUE = 'Describe Issue',
  ADDITIONAL_DETAILS = 'Additional Details',
  CREATE_CASE = 'Create Case',
}

export const supportFormSections = [
  {
    section: SupportFormSection.DESCRIBE_ISSUE,
    textKey: 'support_form_describe_issue_section',
    fallbackTect: 'Describe Issue',
  },
  {
    section: SupportFormSection.ADDITIONAL_DETAILS,
    textKey: 'support_form_additional_details_section',
    fallbackTect: 'Additional Details',
  },
  {
    section: SupportFormSection.CREATE_CASE,
    textKey: 'support_form_create_case_section',
    fallbackTect: 'Create Case',
  },
];

export enum VersionDropdownField {
  STUDIO_OR_ROBOT_VERSION = 'studioOrRobotVersion',
  ORCHESTRATOR_VERSION = 'orchestratorVersion',
  PRODUCT_COMPONENT_VERSION = 'productComponentVersion',
  AUTOMATION_SUITE_VERSION = 'automationSuiteVersion',
  NONE = 'none',
}

export const studioAndRobotVersionsProduct = [ 'Studio', 'Studio X', 'Robot' ];

export const orchestratorProduct = [ 'Orchestrator' ];

export const COMMUNITY_LICENSES = [ 'KIT', 'COM' ];

export const publicConfirmationRegisteredUser = {
  title: {
    textKey: 'support_form_public_confirmation_registered_user_text_1',
    fallbackText: '{{email}} is currently an active user of the UiPath Customer Portal.',
  },
  description: {
    textKey: 'support_form_public_confirmation_registered_user_text_2',
    fallbackText: 'This access enables you to monitor and adjust your support cases, delve into a wide range of technical solution articles, download products, and enjoy various other benefits.',
  },
  prompt: {
    textKey: 'support_form_public_confirmation_registered_user_prompt',
    fallbackText: 'Login to your Customer Portal Account',
  },
};

export const publicConfirmationUnRegisteredUserMatchedLicense = {
  title: {
    textKey: 'support_form_public_confirmation_unregistered_user_matched_license_text_1',
    fallbackText: '{{email}} has been successfully added to the UiPath Customer Portal.',
  },
  description: {
    textKey: 'support_form_public_confirmation_unregistered_user_matched_license_text_2',
    fallbackText: 'With this access, you can track and modify your support cases, explore extensive technical solution articles, access product downloads, and more. Finalize your registration by logging in or creating an account with {{email}}.',
  },
  prompt: {
    textKey: 'support_form_public_confirmation_unregistered_user_matched_license_prompt',
    fallbackText: 'Continue with Customer Portal Login',
  },
};

export const publicConfirmationUnRegisteredUserUnMatchedLicense = {
  title: {
    textKey: 'support_form_public_confirmation_unregistered_user_unmatched_license_text_1',
    fallbackText: 'Enhance your support experience by registering for the UiPath Customer Portal.',
  },
  description: {
    textKey: 'support_form_public_confirmation_unregistered_user_unmatched_license_text_2',
    fallbackText: 'By gaining access, you\'ll have the ability to monitor and update your support cases, delve into a wealth of technical solution articles, download various products, and enjoy many other advantages.',
  },
  prompt: {
    textKey: 'support_form_landing_page_register_for_customer_portal',
    fallbackText: 'Register for Customer Portal',
  },
};

// Hides the back button on the support details page.
// Used to hide the back button when the user is redirected into CP from public case creation
export const SUPPORT_DETAILS_HIDE_BACK_QUERY_PARAM = {
  key: 'hide_back',
  value: 'true',
};

export const SAP_SUPPORT_URL = 'https://me.sap.com/createIssue/0';

export enum LogMessageType {
  HAR_TRACE = '.har trace',
  FIDDLER_TRACE = 'fiddler trace',
  DIAGNOSTIC_TOOL_REPORT = 'diagnostic tool report',
  APPLICATION_LOGS = 'application logs',
  SUPPORT_BUNDLE = 'support bundle',
  INSTALL_UPGRADE_COMMAND_AND_OUTPUT = 'install/Upgrade command and output',
  SAMPLE_WORKFLOW_PROJECT = 'sample workflow/project',
}

export const LogMessageTypeText = {
  [LogMessageType.HAR_TRACE]: {
    textKey: 'support_form_attachment_log_message_har_trace',
    fallbackText: '<b>.har trace</b> if the problem can be reproduced in a browser. <a href=\'/kb/000002639\' target=\'_blank\'>Learn how</a>',
  },
  [LogMessageType.FIDDLER_TRACE]: {
    textKey: 'support_form_attachment_log_message_fiddler_trace',
    fallbackText: '<b>Fiddler trace</b> for client app issues (e.g. Assistant, Robot, API calls). <a href=\'/kb/000005131\' target=\'_blank\'>Learn how</a>',
  },
  [LogMessageType.DIAGNOSTIC_TOOL_REPORT]: {
    textKey: 'support_form_attachment_log_message_diagnostic_tool_report',
    fallbackText: '<b>Diagnostic Tool Report</b> for workflow/robot connection issues. <a href=\'/kb/000004601\' target=\'_blank\'>Learn how</a>',
  },
  [LogMessageType.APPLICATION_LOGS]: {
    textKey: 'support_form_attachment_log_message_application_logs',
    fallbackText: '<b>Application logs</b> - under Development tab, select “View log” next to the app used. <a href=\'https://docs.uipath.com/process-mining/automation-cloud/latest/user-guide/viewing-ingestion-logs#saving-the-log-file\' target=\'_blank\'>Learn how</a>',
  },
  [LogMessageType.SUPPORT_BUNDLE]: {
    textKey: 'support_form_attachment_log_message_support_bundle',
    fallbackText: '<b>Support Bundle. </b><a href=\'https://docs.uipath.com/automation-suite/automation-suite/2024.10/installation-guide/using-support-bundle-tool\' target=\'_blank\'>Learn how</a>',
  },
  [LogMessageType.INSTALL_UPGRADE_COMMAND_AND_OUTPUT]: {
    textKey: 'support_form_attachment_log_message_install_upgrade',
    fallbackText: '<b>Install/Upgrade command and output</b> for install/upgrade issues. <a href=\'https://docs.uipath.com/automation-suite/automation-suite/2024.10/installation-guide/manual-installing-automation-suite\' target=\'_blank\'>Learn how</a>',
  },
  [LogMessageType.SAMPLE_WORKFLOW_PROJECT]: {
    textKey: 'support_form_attachment_log_message_sample_workflow_project',
    fallbackText: 'Additionally, add a <b>sample Workflow/Project</b> for processes, activities, dependencies or packages issues.',
  },
};

export const ISSUE_TYPES_NO_LOG_MESSAGE = [ 'None', 'Information' ];
